import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { publicApi, resolveImageUrl } from "../lib/api";
import { Button } from "../components/ui/button";
import { Mail, ArrowRight } from "lucide-react";

export default function AccessoriesPage() {
    const [products, setProducts] = useState([]);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        const fetchAccessories = async () => {
            try {
                const res = await publicApi.getProducts({ category: "accessories" });
                setProducts(res.data);
            } catch (err) {
                console.error("Failed to fetch accessories:", err);
            } finally {
                setLoading(false);
            }
        };
        fetchAccessories();
    }, []);

    return (
        <div className="min-h-[70vh]" data-testid="accessories-page">
            <div className="bg-card border-b border-border">
                <div className="container mx-auto px-6 lg:px-12 py-12 lg:py-20">
                    <p className="font-mono text-xs uppercase tracking-widest text-primary mb-4">Parts & Add-Ons</p>
                    <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl font-black text-foreground tracking-tight mb-4">
                        Accessories
                    </h1>
                    <p className="text-muted-foreground text-base sm:text-lg max-w-2xl">
                        Nozzles, hoses, fittings, protective gear, and replacement parts for your dry ice blasting equipment. Keep your operation running at peak performance.
                    </p>
                </div>
            </div>

            <div className="container mx-auto px-6 lg:px-12 py-12 lg:py-20">
                {loading ? (
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                        {[1, 2, 3].map(i => (
                            <div key={i} className="bg-card border border-border h-80 animate-pulse" />
                        ))}
                    </div>
                ) : products.length > 0 ? (
                    <>
                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                            {products.map(product => (
                                <Link
                                    key={product.id}
                                    to={`/products/${product.id}`}
                                    className="group bg-card border border-border hover:border-primary/50 transition-all duration-300"
                                    data-testid={`accessory-card-${product.id}`}
                                >
                                    <div className="aspect-square bg-muted/30 overflow-hidden flex items-center justify-center p-6">
                                        {product.images?.[0] ? (
                                            <img
                                                src={resolveImageUrl(product.images[0])}
                                                alt={product.display_title}
                                                className="w-full h-full object-contain group-hover:scale-105 transition-transform duration-300"
                                            />
                                        ) : (
                                            <div className="text-muted-foreground text-sm">No image</div>
                                        )}
                                    </div>
                                    <div className="p-5 border-t border-border">
                                        <p className="font-mono text-xs uppercase tracking-widest text-primary mb-2">
                                            Manufactured by {product.manufacturer} · Official Sales & Service by {product.sales_service_provider || "Dry Ice Bulls"}
                                        </p>
                                        <h3 className="font-heading text-lg font-bold text-foreground mb-2 group-hover:text-primary transition-colors">
                                            {product.display_title}
                                        </h3>
                                        <p className="text-muted-foreground text-sm line-clamp-2 mb-4">
                                            {product.short_description}
                                        </p>
                                        <div className="flex items-center text-primary text-sm font-mono uppercase tracking-wider">
                                            View Details <ArrowRight className="ml-2 h-4 w-4 group-hover:translate-x-1 transition-transform" />
                                        </div>
                                    </div>
                                </Link>
                            ))}
                        </div>

                        <div className="mt-12 bg-card border border-border p-8 text-center">
                            <p className="text-muted-foreground text-sm mb-4">
                                Looking for something specific? We stock a full range of accessories, new and used machines from all brands.
                            </p>
                            <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
                                <Link to="/quote">
                                    <Button
                                        className="bg-primary text-primary-foreground hover:bg-primary/90 rounded-none uppercase font-bold tracking-wider px-8"
                                        data-testid="accessories-quote-btn"
                                    >
                                        Request Accessories Quote
                                    </Button>
                                </Link>
                                <a href="mailto:sales@dryicemarketplace.com" className="flex items-center gap-2 text-muted-foreground hover:text-primary transition-colors text-sm">
                                    <Mail className="h-4 w-4" />
                                    sales@dryicemarketplace.com
                                </a>
                            </div>
                        </div>
                    </>
                ) : (
                    <div className="bg-card border border-border p-8 lg:p-12 text-center max-w-2xl mx-auto">
                        <div className="w-16 h-16 bg-primary/10 border border-primary/20 flex items-center justify-center mx-auto mb-6">
                            <span className="font-heading font-black text-primary text-2xl">!</span>
                        </div>
                        <h2 className="font-heading text-lg font-bold text-foreground mb-3">
                            Full Accessories Catalog
                        </h2>
                        <p className="text-muted-foreground text-sm leading-relaxed mb-6">
                            We stock a full range of accessories, new and used machines from all brands. Contact us for availability and pricing.
                        </p>
                        <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
                            <Link to="/quote">
                                <Button
                                    className="bg-primary text-primary-foreground hover:bg-primary/90 rounded-none uppercase font-bold tracking-wider px-8"
                                    data-testid="accessories-quote-btn"
                                >
                                    Request Accessories Quote
                                </Button>
                            </Link>
                            <a href="mailto:sales@dryicemarketplace.com" className="flex items-center gap-2 text-muted-foreground hover:text-primary transition-colors text-sm">
                                <Mail className="h-4 w-4" />
                                sales@dryicemarketplace.com
                            </a>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
}
